"use client"

import { useState } from "react"
import { Form, Formik } from "formik"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { getAiDisclosureDetailsAction, getAiDisclosureHistoryAction } from "@/server-actions/ai-disclosure-settings"
import { disclosureDetailsCsv, disclosureCsvHeader } from "@/lib/ai-disclosure/report-csv"
import type { DisclosureDetail } from "@/lib/ai-disclosure/report"
import { DisclosureReportTables } from "@/components/organization/disclosure-report-tables"

export function AiDisclosureReport() {
  const [range, setRange] = useState(() => {
    const today = new Date()
    const start = new Date(today)
    start.setUTCDate(start.getUTCDate() - 30)
    return { from: isoDay(start), to: isoDay(today) }
  })
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState<string | null>(null)
  const from = `${range.from}T00:00:00.000Z`
  const to = afterDay(range.to)

  const exportCsv = async () => {
    setExporting(true)
    setExportError(null)
    try {
      const lines = [disclosureCsvHeader]
      const details: DisclosureDetail[] = []
      const history = await getAiDisclosureHistoryAction({ from, to })
      if ("status" in history) throw new Error(history.errorMessage)
      let cursor: string | undefined
      do {
        const result = await getAiDisclosureDetailsAction({ from, to, cursor })
        if ("status" in result) throw new Error(result.errorMessage)
        details.push(...result.details)
        cursor = result.nextCursor ?? undefined
      } while (cursor)
      lines.push(...disclosureDetailsCsv(details, history))
      const url = URL.createObjectURL(new Blob([lines.join("\r\n")], { type: "text/csv;charset=utf-8" }))
      const link = document.createElement("a")
      link.href = url
      link.download = `ai-disclosure-details-${range.from}-${range.to}.csv`
      link.click()
      URL.revokeObjectURL(url)
    } catch (error) {
      setExportError(error instanceof Error ? error.message : "Could not export disclosure details.")
    } finally {
      setExporting(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>AI Disclosure delivery report</CardTitle>
        <CardDescription>
          <span>Review disclosure evidence and missing records to support compliance with </span>
          <a href="https://ai-act-service-desk.ec.europa.eu/en/ai-act/article-50" target="_blank" rel="noopener noreferrer" className="underline underline-offset-4 hover:text-foreground">EU AI Act transparency requirements (Article 50)</a>
          <span>. Filter persisted AI interactions by UTC date range. Coverage is not a compliance certificate or proof of disclosure at first contact.</span>
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Formik initialValues={range} onSubmit={values => { setRange(values); setExportError(null); return Promise.resolve() }}>
          {formik => (
            <Form className="flex flex-wrap items-end gap-3">
              <div className="space-y-1">
                <Label htmlFor="ai-report-from">From (UTC)</Label>
                <Input id="ai-report-from" type="date" name="from" value={formik.values.from}
                  max={formik.values.to} onChange={formik.handleChange} required />
              </div>
              <div className="space-y-1">
                <Label htmlFor="ai-report-to">To (UTC)</Label>
                <Input id="ai-report-to" type="date" name="to" value={formik.values.to}
                  min={formik.values.from} onChange={formik.handleChange} required />
              </div>
              <Button type="submit" disabled={formik.isSubmitting || exporting}>Apply</Button>
              <Button type="button" variant="outline" disabled={exporting} onClick={() => void exportCsv()}>
                <span key={String(exporting)}>{exporting ? "Exporting…" : "Export detailed CSV"}</span>
              </Button>
            </Form>
          )}
        </Formik>
        {exportError && <p role="alert" className="text-sm text-destructive">{exportError}</p>}
        <p className="text-xs text-muted-foreground">
          Counts distinct conversations with persisted AI interactions or disclosure events.
          Disabled records are excluded from coverage; missing records mean unknown, not confirmed non-disclosure.
          Earlier evidence is included for continuing conversations; an earlier failure is not hidden by later delivery.
        </p>
        <DisclosureReportTables key={`${from}-${to}`} from={from} to={to} />

      </CardContent>
    </Card>
  )
}

function isoDay(date: Date): string {
  return date.toISOString().slice(0, 10)
}

function afterDay(day: string): string {
  const date = new Date(`${day}T00:00:00.000Z`)
  date.setUTCDate(date.getUTCDate() + 1)
  return date.toISOString()
}
